import { useQueryClient } from "@tanstack/react-query";
import { memo, useRef } from "react";
import { toast } from "react-hot-toast";
import { useTickerContext } from "@/contexts/TickerContext";
// ❌ REMOVED: useAuth import (v0.66.0 - no longer needed)
import { activityStatsKeys } from "@/hooks/useActivityStats";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { memoKeys } from "@/hooks/useJournalQueries";
import { tickerKeys } from "@/hooks/useTickerQueries";
import { userKeys } from "@/hooks/useUserQueries";
import { extractTickerSymbols } from "@/lib/lexical-utils";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";
// ❌ REMOVED: convertVisibilityFromString import (v0.66.0 - all memos are private)
import { EditorContent, EditorMetadata, EditorToolbar, FocusModeExitButton, FocusModeOverlay } from "./components";
import { FOCUS_MODE_STYLES } from "./constants";
import type { EditorRefActions } from "./Editor";
import { useAutoSave, useFocusMode, useJournalInit, useKeyboard } from "./hooks";
import { cacheService, journalService, validationService } from "./services";
import { EditorProvider, useEditorContext } from "./state";
import type { JournalEditorProps } from "./types";

const JournalEditor = (props: JournalEditorProps) => {
  const { className, cacheKey, memoName, parentMemoName, autoFocus, placeholder, initialTicker, onConfirm, onCancel } = props;

  return (
    <EditorProvider sessionKey={cacheKey}>
      <MemoizedJournalEditorImpl
        className={className}
        cacheKey={cacheKey}
        memoName={memoName}
        parentMemoName={parentMemoName}
        autoFocus={autoFocus}
        placeholder={placeholder}
        initialTicker={initialTicker}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </EditorProvider>
  );
};

const JournalEditorImpl: React.FC<JournalEditorProps> = ({
  className,
  cacheKey,
  memoName,
  parentMemoName,
  autoFocus,
  placeholder,
  initialTicker,
  onConfirm,
  onCancel,
}) => {
  const t = useTranslate();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const editorRef = useRef<EditorRefActions>(null);
  const { state, actions, dispatch } = useEditorContext();
  const { symbol: currentTickerSymbol } = useTickerContext();
  // ❌ REMOVED: userGeneralSetting (v0.66.0 - no longer needed)

  // ❌ REMOVED: defaultVisibility logic (v0.66.0 - all memos are private)

  useJournalInit(editorRef, memoName, cacheKey, currentUser?.name ?? "", autoFocus, initialTicker);

  // Auto-save content to localStorage
  useAutoSave(state.content, currentUser?.name ?? "", cacheKey);

  // Focus mode management with body scroll lock
  useFocusMode(state.ui.isFocusMode);

  const handleToggleFocusMode = () => {
    dispatch(actions.toggleFocusMode());
  };

  useKeyboard(editorRef, { onSave: handleSave });

  // v0.69.0: Use error handler with i18n support
  const { showError } = useErrorHandler();

  async function handleSave() {
    // Validate before saving
    const { valid, reason } = validationService.canSave(state);
    if (!valid) {
      toast.error(reason || t("message.validation.cannot-save"));
      return;
    }

    dispatch(actions.setLoading("saving", true));

    try {
      const result = await journalService.save(state, { memoName, parentMemoName });

      if (!result.hasChanges) {
        toast.error(t("editor.no-changes-detected"));
        onCancel?.();
        return;
      }

      // Clear localStorage cache on successful save
      cacheService.clear(cacheService.key(currentUser?.name ?? "", cacheKey));

      // Invalidate React Query cache to refresh memo lists across the app
      const invalidationPromises = [
        queryClient.invalidateQueries({ queryKey: memoKeys.lists() }),
        // Invalidate user stats - invalidate all stats keys to catch both old and new query patterns
        queryClient.invalidateQueries({ queryKey: userKeys.all }),
        // Invalidate activity stats for calendar heatmap
        queryClient.invalidateQueries({ queryKey: activityStatsKeys.all }),
      ];

      // Note: We only invalidate the AI version since that's what MemoDetail uses for the main memo
      if (memoName) {
        invalidationPromises.push(queryClient.invalidateQueries({ queryKey: memoKeys.detail(memoName, true) }));
      }

      // If memo contains tickers, refetch ticker cache immediately
      const tickerSymbols = extractTickerSymbols(state.content);
      if (tickerSymbols.length > 0) {
        invalidationPromises.push(queryClient.refetchQueries({ queryKey: tickerKeys.lists() }));
      }

      // If we're viewing a TickerDetail page, invalidate that ticker's detail query:
      // - For NEW journals (no memoName): Only refresh if journal mentions this ticker
      // - For EXISTING journals (has memoName): Always refresh (might be removing ticker mentions)
      // - Archive/pin/delete via useUpdateMemo: Always refresh (unconditional)
      if (currentTickerSymbol) {
        const isEditingExistingMemo = !!memoName;
        const journalMentionsCurrentTicker = tickerSymbols.includes(currentTickerSymbol);

        if (isEditingExistingMemo || journalMentionsCurrentTicker) {
          invalidationPromises.push(queryClient.invalidateQueries({ queryKey: tickerKeys.detail(currentTickerSymbol) }));
        }
      }

      // DEPRECATED: v0.73.0 - Comment feature removed
      // If this was a comment, also invalidate the comments query for the parent memo
      // if (parentMemoName) {
      //   invalidationPromises.push(queryClient.invalidateQueries({ queryKey: memoKeys.comments(parentMemoName) }));
      // }

      await Promise.all(invalidationPromises);

      // IMPORTANT: Reset Lexical editor BEFORE dispatching state reset
      // This ensures decorator nodes are properly cleaned up before state changes
      editorRef.current?.reset();

      // Reset editor state to initial values (after editor is cleared)
      dispatch(actions.reset());

      // Notify parent component of successful save
      onConfirm?.(result.memoName);
    } catch (error) {
      // v0.69.0: Use showError hook with i18n support
      showError(error);
    } finally {
      dispatch(actions.setLoading("saving", false));
    }
  }

  return (
    <>
      <FocusModeOverlay isActive={state.ui.isFocusMode} onToggle={handleToggleFocusMode} />

      {/*
        Layout structure:
        - Uses justify-between to push content to top and bottom
        - In focus mode: becomes fixed with specific spacing, editor grows to fill space
        - In normal mode: stays relative with max-height constraint
      */}
      <div
        className={cn(
          "group relative w-full flex flex-col justify-between items-start bg-card px-4 pt-3 pb-1 rounded-lg border border-border gap-2",
          FOCUS_MODE_STYLES.transition,
          state.ui.isFocusMode && cn(FOCUS_MODE_STYLES.container.base, FOCUS_MODE_STYLES.container.spacing),
          className,
        )}
      >
        {/* Exit button is absolutely positioned in top-right corner when active */}
        <FocusModeExitButton isActive={state.ui.isFocusMode} onToggle={handleToggleFocusMode} title={t("editor.exit-focus-mode")} />

        {/* Editor content grows to fill available space in focus mode */}
        <EditorContent ref={editorRef} placeholder={placeholder} autoFocus={autoFocus} editorInstanceId={cacheKey} />

        {/* Metadata and toolbar grouped together at bottom */}
        <div className="w-full flex flex-col gap-2 relative z-10">
          <EditorMetadata memoName={memoName} />
          <EditorToolbar onSave={handleSave} onCancel={onCancel} memoName={memoName} editorRef={editorRef} cacheKey={cacheKey} />
        </div>
      </div>
    </>
  );
};

// Memoize JournalEditorImpl to prevent unnecessary re-renders
// Custom comparison to ensure it only re-renders when props actually change
const MemoizedJournalEditorImpl = memo(JournalEditorImpl, (prevProps, nextProps) => {
  return (
    prevProps.className === nextProps.className &&
    prevProps.cacheKey === nextProps.cacheKey &&
    prevProps.memoName === nextProps.memoName &&
    prevProps.parentMemoName === nextProps.parentMemoName &&
    prevProps.autoFocus === nextProps.autoFocus &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.onCancel === nextProps.onCancel &&
    prevProps.onConfirm === nextProps.onConfirm
  );
});

// Use React.memo to prevent unnecessary re-renders when parent (PagedMemoList) re-renders
// due to filter changes. This ensures the editor only re-renders when its props actually change.
// Custom comparison function to shallowly compare props.
export default memo(JournalEditor, (prevProps, nextProps) => {
  return (
    prevProps.cacheKey === nextProps.cacheKey &&
    prevProps.className === nextProps.className &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.onCancel === nextProps.onCancel &&
    prevProps.onConfirm === nextProps.onConfirm &&
    prevProps.memoName === nextProps.memoName &&
    prevProps.parentMemoName === nextProps.parentMemoName &&
    prevProps.autoFocus === nextProps.autoFocus
  );
});
